import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import {
  getImage,
  setStatus,
  uploadImages,
  getImageStatus,
  setResubmit,
} from "../../redux/slices/carrommSlice";
import { useCarromm } from "../../context/carromm";
import {
  CLOUD_URL
} from "../../constants/common.constants";
import axios from "axios";
import Heading from "../../components/heading/Heading";

const chunkSize = 10 * 1024;
const Result = () => {
  const {
    previewImages,
    bg_id,
    number_plate,
    input_url,
    loading,
    output_url,
    status,
    sku_id,
    dimension,
    showroom,
    isResubmit,
  } = useSelector((state) => state.carromm);
  const dispatch = useDispatch();
  const carrom = useCarromm();
  const [files, setFiles] = useState([]);
  const [currentFileIndex, setCurrentFileIndex] = useState(null);
  const [lastUploadedFileIndex, setLastUploadedFileIndex] = useState(null);
  const [currentChunkIndex, setCurrentChunkIndex] = useState(null);
  const [currentProgress, setCurrentProgress] = useState({
    stage: 0,
    progressTitle: "",
  });
  const [isDownloading, setIsDownloading] = useState(false);

  const processImage = async(input) => {
    setCurrentProgress({ stage: 2, progressTitle: "Senden an API" });
    if (input.type === "image" && !carrom.input) {
      toast.error("Bitte wählen Sie zuerst eine Datei aus");
      setCurrentProgress({ stage: -1, progressTitle: "Fehler" });
      return;
    } else if (input.type === "url" && !input.value) {
      setCurrentProgress({ stage: -1, progressTitle: "Fehler" });
      toast.error("Bild in verfügbar");
      return;
    }
    let formdata = new FormData();
    formdata.append("image_url", input.value);
    formdata.append("scene_id", showroom);
    if (number_plate?.thumbnail !== "") {
      const licensePlate = await convertImagetoBytes(
        number_plate?.thumbnail,
        number_plate?.name
      );
      formdata.append("license_plate", licensePlate);
    }
    if (dimension === "2D" && bg_id?.id !== "-") {
      const backgroundPlate = await convertImagetoBytes(
        bg_id?.thumbnail,
        bg_id?.name
      );
      formdata.append("background", backgroundPlate);
    }
    if (isResubmit) {
      formdata.append("resubmit", true);
    }
    if (files?.[0]?.name) {
      formdata.append("vehicle_id", getImageName(files?.[0]?.name));
    }
    setCurrentProgress({ stage: 3, progressTitle: "Warten auf API-Antwort" });
    dispatch(uploadImages(formdata))
      .unwrap()
      .then((res) => {
        console.log("upload image res", res);
        if (res?.data?.images?.[0]?.status === "error") {
          setCurrentProgress({ stage: -1, progressTitle: "Fehler" });
        }
      })
      .catch((error) => toast.error(`Bild-Upload fehlgeschlagen: ${error}`));
  };
  const handleUpload = () => {
    if (!carrom.input) {
      toast.error("Bitte wählen Sie zuerst eine Datei aus");
      return;
    }
    setFiles([carrom.input]);
  };

  const convertImagetoBytes = async (bgUrl, fileName) => {
    try {
      // Fetch the image from the URL
      const response = await fetch(bgUrl);
      const blob = await response.blob();

      // Convert Blob to File (optional, but helps with naming)
      const file = new File([blob], fileName, { type: blob.type });

      return file;
    } catch (error) {
      return error;
    }
  };

  useEffect(() => {
    let timer;
    if (status === "processing") {
      timer = setInterval(() => {
        dispatch(getImageStatus(sku_id));
      }, 5000);
    } else if (status === "raw" || status === "final") {
      dispatch(getImage(sku_id))
        .unwrap()
        .then(() => {
          setCurrentProgress({ stage: 4, progressTitle: "Erledigt" });
          dispatch(setResubmit(true));
          setTimeout(() => {
            setFiles([]);
          }, 1000);
        })
        .catch((error) =>
          toast.error(
            `Beim Abrufen des Bildes ist ein Fehler aufgetreten: ${error}`
          )
        );
    } else if (status === "error") {
      toast.error(`Error occured while getting status of image`);
    } else if (status === "restart") {
      setFiles([]);
      setCurrentFileIndex(null);
      setLastUploadedFileIndex(null);
      setCurrentChunkIndex(null);
      setCurrentProgress({ stage: 0, progressTitle: "" });
      setIsDownloading(false);
      dispatch(setStatus(""));
    }
    return () => {
      clearInterval(timer);
    };
  }, [sku_id, status]);

  function readAndUploadCurrentChunk() {
    const reader = new FileReader();
    const file = files[currentFileIndex];
    if (!file) {
      return;
    }
    const from = currentChunkIndex * chunkSize;
    const to = from + chunkSize;
    const blob = file.slice(from, to);
    reader.onload = (e) => uploadChunk(e);
    reader.readAsDataURL(blob);
  }

  function uploadChunk(readerEvent) {
    const file = files[currentFileIndex];
    const data = readerEvent.target.result;
    const params = new URLSearchParams();
    params.set("name", file.name);
    params.set("size", file.size);
    params.set("currentChunkIndex", currentChunkIndex);
    params.set("totalChunks", Math.ceil(file.size / chunkSize));
    const headers = { "Content-Type": "application/octet-stream" };
    const url = `${CLOUD_URL}/upload?${params.toString()}`;
    axios.post(url, data, { headers }).then((response) => {
      const file = files[currentFileIndex];
      const filesize = files[currentFileIndex].size;
      const chunks = Math.ceil(filesize / chunkSize) - 1;
      const isLastChunk = currentChunkIndex === chunks;
      if (isLastChunk) {
        file.finalFilename = response.data.finalFilename;
        setLastUploadedFileIndex(currentFileIndex);
        setCurrentChunkIndex(null);
        processImage({
          type: "url",
          value: `${CLOUD_URL}/uploads/${response.data.finalFilename}`,
        });
      } else {
        setCurrentChunkIndex(currentChunkIndex + 1);
      }
    });
  }

  useEffect(() => {
    if (lastUploadedFileIndex === null) {
      return;
    }
    const isLastFile = lastUploadedFileIndex === files.length - 1;
    const nextFileIndex = isLastFile ? null : currentFileIndex + 1;
    setCurrentFileIndex(nextFileIndex);
  }, [lastUploadedFileIndex]);

  useEffect(() => {
    if (files.length > 0) {
      if (currentFileIndex === null) {
        setCurrentFileIndex(
          lastUploadedFileIndex === null ? 0 : lastUploadedFileIndex + 1
        );
        setCurrentProgress({ stage: 1, progressTitle: "Hochladen..." });
      }
    }
  }, [files.length]);

  useEffect(() => {
    if (currentFileIndex !== null) {
      setCurrentChunkIndex(0);
    }
  }, [currentFileIndex]);

  useEffect(() => {
    if (currentChunkIndex !== null) {
      readAndUploadCurrentChunk();
    }
  }, [currentChunkIndex]);

  const getImageName = (fileName) => {
    const lastDotIndex = fileName.lastIndexOf(".");

    // If no dot is found, return the file name and an empty extension
    if (lastDotIndex === -1) {
      return fileName;
    }

    const name = fileName.slice(0, lastDotIndex);
    return name;
  };

  const handleDownload = async () => {
    const fileName = output_url.split("/").pop();
    if (!fileName) {
      toast.error("Download file not found");
      return;
    }
    setIsDownloading(true);
    try {
      const a = document.createElement("a");
      a.href = output_url;
      a.download = fileName;

      document.body.appendChild(a);
      a.click();

      document.body.removeChild(a);

      window.URL.revokeObjectURL(output_url);
      setIsDownloading(false);
    } catch (error) {
      toast.error("Error downloading file:", error.message);
      setIsDownloading(false);
    }
  };
  const handleReset = () => {
    window.location.reload();
  };
  return (
    <>
      <Heading />
      <div className="before-after-block">
        <div className="before-process-box">
          <h2 className="title-20">Original</h2>
          <div className="img-holder">
            <img
              src={
                previewImages && previewImages?.[0] && previewImages?.[0]?.url
                  ? previewImages?.[0]?.url
                  : input_url
              }
              alt=""
            />
          </div>
          {files?.[0]?.name && (
            <div className="name">
              Vehicle ID: {getImageName(files?.[0]?.name)}
            </div>
          )}
        </div>
        <div className="after-process-box">
          <h2 className="title-20">Ergebnis</h2>
          <div className="img-holder">
            {!(loading || status === "processing" || status === "accepted") ? (
              <>
                {output_url ? (
                  <img src={output_url} alt={output_url} />
                ) : (
                  <img
                    src="https://carromm.s3.amazonaws.com/raw/number_plate.png"
                    alt="https://carromm.s3.amazonaws.com/raw/number_plate.png"
                  />
                )}
              </>
            ) : (
              <div className="img_loader">
                <img
                  src="./assets/image/car-processor.gif"
                  alt="./assets/image/car-processor.gif"
                />
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="process-btn-wrap w-100">
        {(status === "" || status === "restart") && carrom.input ? (
          <button className="btn btn-process" onClick={handleUpload}>
            <div className="flex gap-2 my-auto">
              <img
                src="./assets/image/processing.png"
                alt=""
                className=""
                style={{ width: "32px" }}
              />{" "}
              <span>Jetzt optimieren</span>
            </div>
          </button>
        ) : (
          <button
            className="btn btn-process flex flex-wrap gap-2"
            onClick={handleReset}
          >
            <div className="flex gap-2 my-auto">
              <img
                src="./assets/image/icon/reset.svg"
                alt=""
                className=""
                style={{ width: "32px", filter: "invert(1)" }}
              />{" "}
              <span>Zur Startseite</span>
            </div>
          </button>
        )}

        <button
          className={`btn btn-process ms-2 ${output_url ? "" : "opacity-25"}`}
          disabled={!output_url}
          onClick={handleDownload}
        >
          {isDownloading ? (
            <>
              <span
                className="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
              ></span>{" "}
              Herunterladen...
            </>
          ) : (
            <>
              <div className="flex gap-2 my-auto">
                <img
                  src="./assets/image/icon/download.svg"
                  alt=""
                  className=""
                  style={
                    output_url
                      ? { width: "32px", filter: "invert(1)" }
                      : { width: "32px" }
                  }
                />{" "}
                <span>herunterladen</span>
              </div>
            </>
          )}
        </button>
      </div>

      <div className="files">
        {files.map((file, fileIndex) => {
          let progress = 0;
          if (file.finalFilename) {
            switch (currentProgress.stage) {
              case 2:
                progress = 70;
                break;
              case 3:
                progress = 80;
                break;
              case 4:
                progress = 100;
                break;
              default:
                progress = 100;
                break;
            }
          } else {
            const uploading = fileIndex === currentFileIndex;
            const chunks = Math.ceil(file.size / chunkSize);
            if (uploading) {
              progress = Math.round((currentChunkIndex / chunks) * 60);
            } else {
              progress = 0;
            }
          }
          return (
            <a
              className="file"
              target="_blank"
              href={`${CLOUD_URL}/uploads/${file.finalFilename}`}
              key={fileIndex + file?.lastModifiedDate}
            >
              <div className="name">{file.name}</div>
              <div
                className={"progress " + (progress === 100 ? "done" : "")}
                style={{ width: progress + "%" }}
              ></div>
              <div className="progressText">
                {currentProgress.progressTitle} {progress}%
              </div>
            </a>
          );
        })}
      </div>
    </>
  );
};

export default Result;
